import { Inotification } from '@typings/notification/notification.interface';
import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import Avatar from '@components/dataDisplay/Avatar';
import { useDocument } from '@nandorojo/swr-firestore';
import { User } from '@typings/setting/userType.interface';
import { CollectionPath } from '@typings/collections.enum';
import { useNavigate } from 'react-router-dom';
import { updateNotiRead } from '@store/firebase/notification/add-notification';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import NotificationMessageMapper from './NotificationMessageMapper';
import Icon from '@components/dataDisplay/Icons/Icon';
import Tooltip from '@components/dataDisplay/Tooltip';
import TimeAgo from '@components/dataDisplay/TimeAgo';
import { removeNotification } from '@store/firebase/notification/remove-notification';

const ItemContainer = styled.li<{ is_seen: boolean }>`
  height: auto;
  background: ${({ is_seen }) => !is_seen && `var(--inkColor)`};
  list-style: none;
  border-bottom: 1px solid var(--lineColor);
  padding: 16px 16px 16px 12px;
  transition: 0.2s;
  position: relative;

  &:hover {
    background: var(--inkColor);
    cursor: var(--hoverCursorURL), pointer;

    .deleteSec {
      display: block;
    }
  }
`;

const Content = styled.div`
  display: flex;
  width: 100%;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 8px;
  width: calc(100% - 40px);
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 4px;
  pointer-events: none;
  font-size: 12px;
  color: var(--subTextColor);
`;

const ImageContainer = styled.div`
  position: relative;
  margin-top: 4px;
`;

const DeleteBtnSec = styled.div`
  display: none;
  position: absolute;
  right: 12px;
  top: 12px;
`;

const DeleteBtn = styled.button`
  background-color: var(--buttonBackgroundColor);
  width: 24px;
  height: 24px;
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: var(--inkColor);
  }
`;

type Props = {
  data: Inotification;
};
function NotiItem({ data }: Props) {
  const navigate = useNavigate();
  const currentUserUid = useRecoilValue(currentUserIDState);
  const didNavigateRef = useRef(false);

  const {
    sender,
    receiver,
    collectionPath,
    postId,
    targetPath,
    is_seen,
    createdAt,
    id: notificationID,
  } = data;

  const { data: senderData } = useDocument<User>(
    `${CollectionPath.USERS}/${sender}`,
    { listen: true },
  );

  const { data: receiverData } = useDocument<User>(
    `${CollectionPath.USERS}/${receiver}`,
    { listen: true },
  );

  const { data: postData } = useDocument<any>(`${collectionPath}/${postId}`, {
    listen: true,
  });

  const numberId: number = postData?.numberId ?? 0;

  const handleActivate = useCallback(() => {
    if (didNavigateRef.current) return;
    didNavigateRef.current = true;
    // navigate first for better responsiveness on some Android tablets
    if (targetPath) {
      navigate(targetPath);
    } else {
      navigate(`/${collectionPath}/${postId}`);
    }
    // fire-and-forget read update; do not block navigation
    updateNotiRead({ userId: currentUserUid, notificationID }).catch(() => {});
    // allow subsequent activations after short delay (defensive)
    setTimeout(() => {
      didNavigateRef.current = false;
    }, 800);
  }, [navigate, collectionPath, postId, targetPath, currentUserUid, notificationID]);

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      removeNotification({ userId: currentUserUid, notificationID });
    },
    [currentUserUid, notificationID],
  );

  return (
    <>
      <ItemContainer
        is_seen={is_seen || false}
        role="button"
        tabIndex={0}
        onClick={handleActivate}
        onPointerUp={handleActivate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleActivate();
        }}
      >
        <Content>
          <ImageContainer>
            <Avatar size="32px" photoURL={senderData?.photoURL} />
          </ImageContainer>
          <Info>
            <Header>
              <TimeAgo createdAt={createdAt} />
            </Header>

            <NotificationMessageMapper
              postData={postData}
              data={data}
              senderData={senderData!}
              receiverData={receiverData}
              numberId={numberId}
            />
          </Info>
        </Content>
        <DeleteBtnSec className="deleteSec">
          <Tooltip message="알림 삭제" direction="left">
            <DeleteBtn onClick={handleDelete}>
              <Icon name="X12" color="var(--buttonTextColor)" />
            </DeleteBtn>
          </Tooltip>
        </DeleteBtnSec>
      </ItemContainer>
    </>
  );
}

export default React.memo(NotiItem);
